import { useQuery } from '@tanstack/react-query';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LayoutDashboard } from 'lucide-react';
import { parseAsString, useQueryStates } from 'nuqs';
import { useParams } from 'react-router';
import type { DashboardTileId, DashboardTileRowDto, DashboardTileRowsDto } from '@pams/shared';
import { isDashboardTileId } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import NotFoundPage from '@/pages/NotFound';
import { api } from '@/lib/api';
import { resolveTileWords } from '@/lib/tileWords';
import { vocabularyWord } from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { DASHBOARD_COPY } from '@/pages/Dashboard/copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHAT A NUMBER IS MADE OF. [P6a]
 *
 *  FR-5.5.5 requires every tile to drill through to its rows. Six drill to `/inbox` and
 *  `/arrangements`, which already read `dateFrom`, `dateTo`, `fillState` and `status` off the
 *  URL. The other eight have no destination in this app at all — there is no attendance list,
 *  no swap list and no per-officer deployment list — and this is where they go.
 *
 *  ⚠️ ONE COMPONENT FOR ALL OF THEM, because the COLUMNS ARE DATA. Eight bespoke screens for
 *  eight tiles that are off by default is a lot of surface to maintain for a question whose
 *  whole answer is "here is the table behind that figure"; P6b grows the per-tile detail from
 *  here when somebody asks for it.
 *
 *  ⚠️ AND THE SERVER RUNS THE SAME TILE. The rows come from the same registry entry the number
 *  did, so a drill list can never disagree with the tile it was opened from — which is the one
 *  failure a drill-through exists to prevent.
 *
 *  ⚠️ THE PARENT LINK IS A REAL `<a>` AND NOT `navigate(-1)`. [D4.3]: `web/src` contains zero
 *  history-back controls, because arriving from a pasted URL makes browser-back land somewhere
 *  unexpected, and a control that is sometimes wrong is worse than none.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const NUQS_OPTIONS = { history: 'replace', clearOnDefault: true } as const;

const DashboardTilePage = () => {
  const { tileId } = useParams<{ tileId: string }>();
  const [range] = useQueryStates(
    { dateFrom: parseAsString.withDefault(''), dateTo: parseAsString.withDefault('') },
    NUQS_OPTIONS,
  );

  const valid: DashboardTileId | null = tileId && isDashboardTileId(tileId) ? tileId : null;
  const params = { dateFrom: range.dateFrom || undefined, dateTo: range.dateTo || undefined };

  const rows = useQuery({
    queryKey: queryKeys.dashboard.rows(valid ?? 'unknown', params),
    queryFn: ({ signal }) =>
      api.get<DashboardTileRowsDto>(`/dashboard/tiles/${String(valid)}/rows`, { query: params, signal }),
    enabled: valid !== null,
  });

  /* ⚠️ An id the catalogue does not know is a 404 here rather than a request that 422s — the
     page can answer it without asking, and a wrong URL should not reach the API at all. */
  if (!valid) return <NotFoundPage />;

  /*
   * ⚠️ [P6b] THE ROWS PAYLOAD CARRIES THE OVERRIDE, so this page cannot show the registry
   * title under a tile the operator renamed. It is `undefined` on the first paint and the
   * heading swaps when the response lands — an honest cost: `keepPreviousData` cannot help
   * because the key includes the tile id, and reading it out of the dashboard's own cache
   * would be right only when the operator arrived by clicking rather than by pasting a URL.
   */
  const words = resolveTileWords(valid, rows.data ?? null);
  const data = rows.data;

  const columns: ColumnsType<DashboardTileRowDto> = (data?.columns ?? []).map((column) => ({
    key: column.key,
    title: column.label,
    align: column.align,
    /*
     * ⚠️ [TODAY-TILE] A CELL THE PROJECTION GAVE AN `href` RENDERS AS A REAL LINK, THROUGH
     * `NameCell` RATHER THAN AN `<a>` WRITTEN HERE.
     *
     * [P2]'s rule — "the first column of a list row OPENS that row… anything that NAVIGATES
     * renders a real `<a>` so ⌘-click and copy-link work" — reached every flat list in the
     * product except this one, because this page's columns are DATA and had no way to say
     * that a cell was a destination. `hrefs` is that way, and it is per CELL rather than per
     * ROW because a row here names two different things (a duty and its order) and they open
     * two different screens.
     *
     * ⚠️ `NameCell` RATHER THAN A LOCAL `<Link>`: the link treatment, the `title` hover and
     * the truncation are one implementation, so this page cannot drift from the eleven lists
     * that already use it.
     *
     * ⚠️ AND A CELL WITH NO `href` STILL RENDERS THE BARE STRING. Passing every cell through
     * `NameCell` would wrap the whole table in a div per cell — a DOM change for seventeen
     * tiles to serve two, and `NameCell`'s no-target arm is a `<span class="truncate">`,
     * which is §8's "cells never truncate" arriving where nobody asked for it.
     *
     * ⚠️ [amended ASSOC-TILE] "TO SERVE ONE" WAS TRUE UNTIL `ASSOCIATED_ORDERS` BECAME THE
     * SECOND ARM OF `projectTileRows` TO SUPPLY `hrefs`. The argument is unchanged and the
     * ratio still carries it; the number is corrected because this file's own subject is a
     * count, and `grep -n 'hrefs:' api/src/services/dashboardRows.service.ts` is what says it.
     */
    /*
     * ⚠️ [C5b §3] A COLUMN THAT NAMES A VOCABULARY IS WORDED HERE — the drill printed
     * `ALLOCATION_IN_PROGRESS` and `COMPLETE` verbatim, so the today tile's drill said `COMPLETE`
     * over a duty the card beside it called `Closed short` (D13). `vocabularyWord` is the card's
     * own English; a column with none prints as sent.
     */
    render: (_: unknown, row: DashboardTileRowDto) => {
      const value = vocabularyWord(column.vocabulary, row.cells[column.key] ?? '');
      const href = row.hrefs?.[column.key];
      return href === undefined || value === '' ? value : <NameCell name={value} to={href} />;
    },
  }));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutDashboard}
        parent={{ label: DASHBOARD_COPY.rowsParent, to: '/dashboard' }}
        title={words.title}
        /*
         * ⚠️ [C1e §3] COMPOSED TO `undefined` WHEN BOTH HALVES ARE ABSENT, NEVER TO AN EMPTY
         * FRAGMENT. `PageHeader` renders `{subtitle && <p …>}` and a non-empty JSX fragment is
         * TRUTHY — so a hidden hint on an untruncated tile would have painted an empty `<p>`,
         * which is a gap the operator asked to remove.
         */
        subtitle={
          words.hint === null && !data?.truncated ? undefined : (
            <>
              {words.hint}
              {data?.truncated ? ` ${DASHBOARD_COPY.truncated(data.items.length, data.total)}` : null}
            </>
          )
        }
      />
      <Table<DashboardTileRowDto>
        size="middle"
        rowKey={(row) => row.key}
        columns={columns}
        dataSource={data?.items ?? []}
        loading={rows.isFetching}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <EmptyState size="sm" title={DASHBOARD_COPY.rowsEmpty} /> }}
      />
    </div>
  );
};

export default DashboardTilePage;
